import styles from "./ServiceSelector.module.css";

import {
  Divider,
  ExternalLinkButton,
  ModuleCategory,
  ModuleId,
  ProductionArea,
  Spinner,
  titles,
  useCurrentProductionArea,
  useModules,
} from "@abb-connect/react-shared";
import ServiceCard from "@components/ServiceCard";

type Props = {
  site: string;
  productionArea?: string;
};

function ServiceSelector({
  site,
  productionArea: requestedProductionArea,
}: Props) {
  const { isLoading, isError, modules } = useModules(
    site,
    requestedProductionArea
  );

  const { productionArea, isLoading: isProductionAreaLoading } =
    useCurrentProductionArea(site, requestedProductionArea);

  if (isProductionAreaLoading || isLoading) {
    return (
      <div className={styles.loader}>
        <Spinner />
      </div>
    );
  }

  if (isError || !modules) {
    return (
      <div className={styles["error-container"]} role="alert">
        <p>An error occurred while loading available services.</p>
      </div>
    );
  }

  const baseServices = modules[ModuleCategory.Base].filter((m) => m.visible);
  const premiumServices = modules[ModuleCategory.Premium].filter(
    (m) => m.visible
  );

  const basePackageEnabled = baseServices.some((s) => s.enabled);
  const performancePackageEnabled = premiumServices.some((s) => s.enabled);
  const isHoistingWithCopilot =
    productionArea === ProductionArea.Hoisting &&
    [...baseServices, ...premiumServices].some(
      (s) => s.id === ModuleId.Copilot && s.enabled
    );

  return (
    <div className={styles.container}>
      {productionArea && (
        <div className={styles.header}>
          <h2>{titles[productionArea]} Connect Services</h2>
          {productionArea && (
            <ExternalLinkButton productionArea={productionArea} />
          )}
        </div>
      )}
      <div className={styles["services-container"]}>
        {baseServices.length > 0 && (
          <div>
            {premiumServices.length > 0 && (
              <Divider
                text={
                  isHoistingWithCopilot ? "Base Package Plus" : "Base Package"
                }
                color={
                  basePackageEnabled
                    ? "var(--foreground-secondary-color)"
                    : "var(--foreground-disabled-color)"
                }
                iconName="Star"
              />
            )}
            <div className={styles["service-list"]}>
              {baseServices.map((service) => (
                <ServiceCard
                  key={service.id}
                  module={service}
                  site={site}
                  productionArea={requestedProductionArea}
                />
              ))}
            </div>
          </div>
        )}
        {premiumServices.length > 0 && (
          <div>
            <Divider
              text="Performance Services"
              color={
                performancePackageEnabled
                  ? "var(--foreground-secondary-color)"
                  : "var(--foreground-disabled-color)"
              }
              iconName="Star"
            />
            <div className={styles["service-list"]}>
              {premiumServices.map((service) => (
                <ServiceCard
                  key={service.id}
                  module={service}
                  site={site}
                  productionArea={requestedProductionArea}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ServiceSelector;
